import { useEffect, useState } from 'react'
import { bootstrap, type AppCore } from '@core/bootstrap'
import { PosPage } from '@core/pages/PosPage'
import { AdminPage } from '@core/pages/AdminPage'
import { PinPadModal, type ActiveCashier } from '@core/components/PinPadModal'
import { ThemeToggle } from '@core/components/ThemeToggle'
import { canOpenAdmin } from '@core/auth/adminAccess'
import { APP_NAME } from '@core/branding'
import { CLOUD_API } from '@core/pages/admin/shared'
import { PairingModal } from '@core/components/PairingModal'
import { syncService } from '@core/services/sync/SyncService'
import { remoteCommandService } from '@core/services/remote/RemoteCommandService'
import { PosEvent } from '@core/bus/events'
import { db } from '@core/db/db'
import {
  META_DEVICE_TOKEN,
  META_LEGACY_AUTH_TOKEN,
  META_TENANT_ID,
  META_DEVICE_PAIRED,
  META_ZONE_ID
} from '@core/db/schema'
import { useIsMobile, POS_COMPACT_BREAKPOINT } from '@core/utils/useIsMobile'
import { cx } from './ui/classes'
import { Close } from './ui/icons'

type View = 'pos' | 'admin'

/** Ключ сессионного кэша активного кассира. */
const SESSION_KEY = 'pos_active_session'
/** TTL сессии кассира — 15 минут. */
const SESSION_TTL_MS = 15 * 60 * 1000

interface StoredSession {
  cashier: ActiveCashier
  lastActive: number
}

/** Прочитать живую (не протухшую) сессию кассира из sessionStorage. */
function loadSession(): ActiveCashier | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSession
    if (!parsed.cashier || typeof parsed.lastActive !== 'number') return null
    if (Date.now() - parsed.lastActive >= SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY)
      return null
    }
    return parsed.cashier
  } catch {
    return null
  }
}

/** Сохранить сессию кассира (обновляя lastActive). */
function saveSession(cashier: ActiveCashier): void {
  try {
    const payload: StoredSession = { cashier, lastActive: Date.now() }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload))
  } catch {
    /* sessionStorage недоступен — тихо игнорируем */
  }
}

/** Обновить только метку активности, если сессия жива. */
function touchSession(): void {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as StoredSession
    parsed.lastActive = Date.now()
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(parsed))
  } catch {
    /* ignore */
  }
}

/** Полностью очистить сессию. */
function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Корневой компонент-«дым». Инициализирует ядро один раз при монтировании
 * и показывает рабочий экран со статусами (POS / Админка).
 */
export function App() {
  const [core, setCore] = useState<AppCore | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<View>('pos')
  const [currentCashier, setCurrentCashier] = useState<ActiveCashier | null>(null)
  const [pairingOpen, setPairingOpen] = useState(false)
  const [cloudToken, setCloudToken] = useState<string>('')
  const [zoneId, setZoneId] = useState<string>('')
  const [pairNotice, setPairNotice] = useState<string | null>(null)
  const [devicePaired, setDevicePaired] = useState(false)
  /**
   * Есть ли локальный справочник сотрудников. Пока его нет (первый запуск),
   * разрешаем только первичную настройку облака. После синка PIN-админка
   * открывается лишь директору/старшему по своему PIN.
   */
  const [hasCashiers, setHasCashiers] = useState<boolean | null>(null)
  // Два независимых порога: «устройство» (плотность/хром, 768) и «лэйаут» кассы (1024).
  // На ноутбуке 900px нужен одноколоночный лэйаут, но стационарный 2D-сканер.
  const isMobile = useIsMobile()
  const compact = useIsMobile(POS_COMPACT_BREAKPOINT)

  /**
   * Вход кассира: сохраняем локальную сессию.
   *
   * Cloud-admin сессия — независимая (см. ARCH-AUTH-AND-ACCESS): вход/выход
   * кассира её не трогает. Доступ к облачным разделам ограничен ролью admin.
   */
  const loginCashier = (cashier: ActiveCashier) => {
    saveSession(cashier)
    setCurrentCashier(cashier)
  }

  /** Явный выход кассира: локальную сессию чистим, облачную — нет. */
  const logoutCashier = () => {
    clearSession()
    setCurrentCashier(null)
    setView('pos')
  }

  // Восстановление сессии кассира из sessionStorage (если не протухла).
  useEffect(() => {
    const restored = loadSession()
    if (restored) {
      setCurrentCashier(restored)
    }
  }, [])

  // Права, выданные/отозванные в облаке, применяются к активной сессии без
  // повторного PIN-входа: справочник кассиров обновляет синк, сессию — из него.
  useEffect(() => {
    if (!currentCashier) return
    let cancelled = false

    const refresh = async () => {
      const record = await db.cashiers.get(currentCashier.user_id)
      if (cancelled || !record) return
      const samePermissions =
        JSON.stringify(record.permissions ?? {}) ===
        JSON.stringify(currentCashier.permissions ?? {})
      if (samePermissions && record.role === currentCashier.role) return
      const updated: ActiveCashier = {
        ...currentCashier,
        permissions: record.permissions,
        role: record.role
      }
      saveSession(updated)
      setCurrentCashier(updated)
    }

    void refresh()
    const off = core?.bus.on(PosEvent.CASHIERS_UPDATED, () => void refresh())
    return () => {
      cancelled = true
      off?.()
    }
  }, [core, currentCashier])

  // Старый admin-JWT не должен оставаться в Dexie после перехода на device-token.
  useEffect(() => {
    void db.meta.delete(META_LEGACY_AUTH_TOKEN).catch(() => undefined)
  }, [])

  // Признак сопряжённого устройства (BYOD) — читаем из meta.
  useEffect(() => {
    void db.meta
      .get(META_DEVICE_PAIRED)
      .then((e) => setDevicePaired(e?.value === true))
      .catch(() => setDevicePaired(false))
  }, [])

  // Справочник сотрудников нужен, чтобы отличить первый запуск от рабочей кассы.
  // После первого синка setup-режим должен закрыться сам.
  useEffect(() => {
    if (!core) return
    const refresh = () => {
      void db.cashiers
        .count()
        .then((count) => setHasCashiers(count > 0))
        .catch(() => setHasCashiers(true))
    }
    refresh()
    return core.bus.on(PosEvent.CASHIERS_UPDATED, refresh)
  }, [core])

  // Первичная настройка допустима только на пустой базе. В рабочей кассе
  // админка открывается по PIN директора/старшего.
  const setupMode = hasCashiers === false
  const adminAvailable = setupMode || canOpenAdmin(currentCashier)

  useEffect(() => {
    if (view === 'admin' && !adminAvailable) setView('pos')
  }, [view, adminAvailable])

  // Продление сессии при активности пользователя + периодически по таймеру.
  useEffect(() => {
    if (!currentCashier) return
    const onActivity = () => touchSession()
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart']
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }))
    const timer = window.setInterval(touchSession, 60_000)
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity))
      clearInterval(timer)
    }
  }, [currentCashier])

  // Если приложение открыто по «/admin» — пробуем показать админку, но
  // `adminAvailable` всё равно потребует PIN директора/старшего (кроме setup).
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const loc = window.location
      if (/admin/i.test(loc.pathname) || /admin/i.test(loc.hash)) {
        setView('admin')
      }
    }
  }, [])

  // Одноразовый pairing-код: QR не содержит device-token.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const code = params.get('pair_code')
    if (!code) return
    const clearUrl = () =>
      window.history.replaceState({}, document.title, window.location.pathname)
    clearUrl()
    void (async () => {
      try {
        const deviceId = await syncService.ensureDeviceId()
        const response = await fetch(`${CLOUD_API}/auth/pair`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, device_id: deviceId })
        })
        if (!response.ok) throw new Error(`Код сопряжения недействителен (${response.status})`)
        const data = (await response.json()) as {
          token: string
          tenant_id: string
          zone_id?: string | null
        }
        await db.meta.put({ key: META_DEVICE_TOKEN, value: data.token })
        await db.meta.put({ key: META_TENANT_ID, value: data.tenant_id })
        await db.meta.put({ key: META_DEVICE_PAIRED, value: true })
        if (data.zone_id) await db.meta.put({ key: META_ZONE_ID, value: data.zone_id })
        setDevicePaired(true)
        setPairNotice('Устройство подключено к рабочему месту (зоне) магазина')
        void syncService.syncAll().catch(() => undefined)
        setCurrentCashier(null)
        setView('pos')
      } catch (error) {
        setPairNotice(error instanceof Error ? error.message : 'Ошибка сопряжения')
      }
    })()
  }, [])

  // Старые QR с pair_token больше не принимаются: только server-side pair_code.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (!params.get('pair_token')) return
    window.history.replaceState({}, document.title, window.location.pathname)
    setPairNotice('Старый QR-код недействителен — сформируйте новый код в кабинете')
  }, [])

  /** Открыть модалку привязки мобильной кассы (нужны токен и tenant из meta). */
  const openPairing = async () => {
    const t = await db.meta.get(META_DEVICE_TOKEN)
    if (!t || typeof t.value !== 'string' || !t.value) {
      setPairNotice('Касса не авторизована в облаке')
      return
    }
    // Зона рабочего места (общий пул тары) гарантируется сервисом синхронизации.
    const zone = await syncService.ensureZoneId()
    setCloudToken(t.value)
    setZoneId(zone)
    setPairingOpen(true)
  }

  useEffect(() => {
    let active = true
    bootstrap()
      .then((c) => {
        if (active) setCore(c)
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      active = false
    }
  }, [])

  // Фоновая синхронизация с облаком: старт при монтировании, стоп при размонтировании.
  useEffect(() => {
    syncService.start(60000)
    remoteCommandService.start()
    return () => {
      remoteCommandService.stop()
      syncService.stop()
    }
  }, [])

  if (error) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 font-sans">
        <h1 className="text-huge font-bold">{APP_NAME}</h1>
        <p className="text-danger">Ошибка инициализации: {error}</p>
      </div>
    )
  }

  if (!core) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 font-sans">
        <h1 className="text-huge font-bold">{APP_NAME}</h1>
        <p className="my-2">Инициализация ядра…</p>
      </div>
    )
  }

  // Касса и мобильный экран — без боковых отступов: окна занимают всю ширину.
  // В кабинете отступы остаются для читаемости таблиц.
  const posFullBleed = view === 'pos'

  return (
    <div
      className={cx(
        'app-safe-top mx-auto min-h-screen w-full overflow-x-hidden bg-page font-sans',
        isMobile
          ? cx('max-w-[100vw] pb-[88px]', posFullBleed ? 'px-0' : 'px-3')
          : compact
            ? cx('max-w-shell pb-[88px]', posFullBleed ? 'px-0' : 'px-6')
            : cx('max-w-shell', posFullBleed ? 'px-0' : 'px-6')
      )}
    >
      <header
        className={cx(
          'flex flex-wrap items-center justify-between gap-2 border-b border-line',
          isMobile ? 'mb-2.5 px-3 pb-2' : 'mb-4 px-4 pb-3'
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex min-w-0 flex-col">
            <h1
              className={cx(
                'm-0 font-display font-bold leading-none tracking-tight',
                isMobile ? 'whitespace-nowrap text-[20px]' : 'text-[28px]'
              )}
            >
              {APP_NAME}
            </h1>
            <p
              className={cx(
                'm-0 leading-tight text-muted',
                isMobile ? 'text-micro' : 'text-tiny'
              )}
            >
              касса — третьей волны
            </p>
          </div>
          {isMobile && (
            <span className="whitespace-nowrap rounded-pill border border-line bg-soft px-2 py-0.5 text-micro font-semibold text-muted2">
              {core.status.jurisdiction} · {core.status.currency}
            </span>
          )}
        </div>
        <nav className="flex flex-wrap items-center justify-end gap-1" role="tablist" aria-label="Разделы">
          {/* Переключатель темы — левее вкладок: это настройка, а не раздел. */}
          <ThemeToggle compact={isMobile} />
          <NavTab
            label={isMobile ? 'Касса' : 'Касса'}
            active={view === 'pos'}
            onClick={() => setView('pos')}
            compact={isMobile}
          />
          {adminAvailable && (
            <NavTab
              label="Кабинет"
              active={view === 'admin'}
              onClick={() => setView('admin')}
              compact={isMobile}
            />
          )}
          {currentCashier && (
            <>
              {isMobile ? (
                <button
                  onClick={logoutCashier}
                  className="inline-flex max-w-[160px] cursor-pointer items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-pill border border-lineStrong bg-surface px-2.5 py-1.5 text-small font-semibold text-ink"
                  title={`Кассир: ${currentCashier.name} — выйти`}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full bg-ok" />
                  <span className="inline-flex items-center gap-1">{currentCashier.name}<Close size={16} /></span>
                </button>
              ) : (
                <button
                  onClick={logoutCashier}
                  className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-lineStrong bg-white px-4 py-2 text-base2 text-ink"
                  title={`Кассир: ${currentCashier.name}`}
                >
                  Выход ({currentCashier.name})
                </button>
              )}
            </>
          )}
        </nav>
      </header>

      {pairNotice && (
        <p
          className="mb-3 rounded-card border border-blue-200 bg-blue-50 px-3 py-2 text-small text-blue-700"
          role="status"
        >
          {pairNotice}
        </p>
      )}
      <main>
        {view === 'admin' && adminAvailable ? (
          <AdminPage
            core={core}
            cashier={currentCashier}
            compact={compact}
            onPairDevice={() => void openPairing()}
            devicePaired={devicePaired}
            setupOnly={setupMode}
          />
        ) : (
          <PosPage
            core={core}
            isMobile={isMobile}
            compact={compact}
            cashierName={currentCashier?.name}
            cashier={currentCashier}
          />
        )}
      </main>

      {/*
        PIN-пад блокирует кассу, пока сотрудник не вошёл. В рабочей кассе
        админка открывается только после PIN директора/старшего. Исключение —
        пустая локальная база: первичная настройка облака.
      */}
      <PinPadModal
        isOpen={currentCashier === null && view !== 'admin'}
        onSuccess={loginCashier}
        onOpenAdmin={setupMode ? () => setView('admin') : undefined}
      />

      {/* Привязка мобильной кассы (QR) — доступна авторизованному кассиру. */}
      <PairingModal
        isOpen={pairingOpen}
        onClose={() => setPairingOpen(false)}
        token={cloudToken}
        zoneId={zoneId}
      />
    </div>
  )
}

/** Кнопка-вкладка навигации с активным состоянием. */
function NavTab({
  label,
  active,
  onClick,
  compact
}: {
  label: string
  active: boolean
  onClick: () => void
  compact?: boolean
}) {
  // Базовый набор БЕЗ цветов: Tailwind не гарантирует порядок утилит, поэтому
  // «активный» и «обычный» варианты задают цвета целиком — иначе bg-white /
  // border-lineStrong могут перебить bg-ink / border-ink (белое на белом).
  const base = cx(
    'inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border transition-colors',
    compact ? 'px-2.5 py-1.5 text-small' : 'px-4 py-2 text-base2'
  )
  const inactive = 'border-lineStrong bg-white text-ink'
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        active ? cx(base, 'cursor-default border-ink bg-ink text-white') : cx(base, inactive)
      }
    >
      {label}
    </button>
  )
}

